import React, { useState, useRef, useEffect } from "react";
import { useDemoMode } from "../../contexts/DemoModeContext";
import "./DemoModeBanner.css";

const PROD_URL = "https://www.ogestyle.com";

/**
 * DemoModeBanner
 * Fixed dismissable banner shown on every page in demo mode.
 * Sets --demo-banner-height CSS var so the fixed site header offsets itself.
 */
export default function DemoModeBanner() {
  const { isDemoMode, demoCard, demoRetentionDays } = useDemoMode();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("demoBannerDismissed") === "true"
  );
  const bannerRef = useRef(null);

  // Keep --demo-banner-height in sync so the fixed header can offset itself
  useEffect(() => {
    const el = bannerRef.current;
    if (!isDemoMode || dismissed || !el) {
      document.documentElement.style.removeProperty("--demo-banner-height");
      return;
    }
    const update = () => {
      document.documentElement.style.setProperty(
        "--demo-banner-height",
        el.offsetHeight + "px"
      );
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty("--demo-banner-height");
    };
  }, [isDemoMode, dismissed]);

  if (!isDemoMode || dismissed) return null;

  function handleDismiss() {
    sessionStorage.setItem("demoBannerDismissed", "true");
    setDismissed(true);
  }

  return (
    <div ref={bannerRef} className="demo-banner" role="banner" aria-label="Demo mode notice">
      <button
        className="demo-banner__close"
        onClick={handleDismiss}
        aria-label="Dismiss demo banner"
      >
        ×
      </button>

      <div className="demo-banner__inner">
        <span className="demo-banner__text">
          <strong>Demo mode.</strong> Use the test card &mdash; never real card
          details.{" "}
          <span className="demo-banner__notice">
            Use a real email for confirmations.
          </span>{" "}
          Demo data deletes after {demoRetentionDays} days.
        </span>

        <div className="demo-banner__actions">
          {demoCard && (
            <div className="demo-banner__card">
              <code>{demoCard.number}</code>
              <span className="demo-banner__card-sep">&middot;</span>
              <code>Exp {demoCard.expiry}</code>
              <span className="demo-banner__card-sep">&middot;</span>
              <code>CVV {demoCard.cvv}</code>
              <span className="demo-banner__card-sep">&middot;</span>
              <code>ZIP {demoCard.zip}</code>
            </div>
          )}

          <a
            className="demo-banner__cta"
            href={PROD_URL}
            rel="noreferrer noopener"
          >
            Create your account <span aria-hidden="true">&rarr;</span>
          </a>
        </div>
      </div>
    </div>
  );
}
